/**
 * Modal para crear/editar cables de fibra óptica
 * @module components/forms/CableFormModal
 */

import { useState, useEffect } from 'react'
import { X, Save, Cable as CableIcon, User, Plug } from 'lucide-react'
import { useNodeStore, useFiberStore } from '@/stores'
import { generarEstructuraFibras } from '@/utils/generarEstructuraFibras'
import { calcularLongitudRuta } from '@/utils/calcularLongitudRuta'

interface CableFormModalProps {
  isOpen: boolean
  onClose: () => void
  origenNodeId?: string
  destinoNodeId?: string
  postesIntermmedios?: string[]
}

export function CableFormModal({
  isOpen,
  onClose,
  origenNodeId,
  destinoNodeId,
  postesIntermmedios = [],
}: CableFormModalProps) {
  const addCable = useFiberStore((state) => state.addCable)
  const updateCable = useFiberStore((state) => state.updateCable)
  const getNodeById = useNodeStore((state) => state.getNodeById)
  const updateNode = useNodeStore((state) => state.updateNode)
  const nodes = useNodeStore((state) => state.nodes)

  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    tipo: 'DISTRIBUCION',
    numeroFibras: '12',
    fabricante: '',
    modelo: '',
  })

  // Estado para conexión DROP (NAP → Cliente)
  const [dropConfig, setDropConfig] = useState({
    puertoNAP: '',
    colorPigtail: 'azul',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  // Obtener información de los nodos
  const origenNode = origenNodeId ? getNodeById(origenNodeId) : null
  const destinoNode = destinoNodeId ? getNodeById(destinoNodeId) : null

  // Detectar si es conexión NAP → Cliente
  const isNAPToCliente = origenNode?.tipo === 'NAP' && destinoNode?.tipo === 'CLIENTE'
  const isClienteToNAP = origenNode?.tipo === 'CLIENTE' && destinoNode?.tipo === 'NAP'
  const isDropConnection = isNAPToCliente || isClienteToNAP
  
  // El nodo NAP (puede ser origen o destino)
  const napNode = isNAPToCliente ? origenNode : (isClienteToNAP ? destinoNode : null)
  const clienteNode = isNAPToCliente ? destinoNode : (isClienteToNAP ? origenNode : null)

  // Obtener puertos de la NAP
  const napConfig = napNode ? ((napNode as any).config || {}) : {}
  const puertosNAP = napConfig.puertos || []
  const numPuertos = napConfig.numPuertos || puertosNAP.length || 8

  // Auto-seleccionar tipo DROP si es conexión NAP-Cliente
  useEffect(() => {
    if (isDropConnection && formData.tipo !== 'DROP') {
      setFormData(prev => ({
        ...prev,
        tipo: 'DROP',
        numeroFibras: '1'
      }))
    }
  }, [isDropConnection])

  if (!isOpen) return null

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!origenNodeId) {
      newErrors.origen = 'Debe seleccionar un nodo de origen'
    }

    if (!destinoNodeId) {
      newErrors.destino = 'Debe seleccionar un nodo de destino'
    }

    const numFibras = parseInt(formData.numeroFibras)
    if (numFibras < 1 || numFibras > 288) {
      newErrors.numeroFibras = 'Debe ser entre 1 y 288'
    }

    // Validar puerto NAP para conexiones DROP
    if (isDropConnection && !dropConfig.puertoNAP) {
      newErrors.puertoNAP = 'Debe seleccionar el puerto de la NAP'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    if (!origenNodeId || !destinoNodeId) {
      alert('Debe seleccionar nodos de origen y destino')
      return
    }

    setLoading(true)

    try {
      // Construir ruta completa: origen -> postes intermedios -> destino
      const ruta: Array<{ lat: number; lng: number; tipo: 'terminal' | 'poste'; nodeId?: string }> = []
      
      // Agregar origen
      ruta.push({ 
        lat: origenNode!.lat, 
        lng: origenNode!.lng, 
        tipo: 'terminal',
        nodeId: origenNodeId
      })

      // Agregar postes intermedios en orden
      postesIntermmedios.forEach(posteId => {
        const poste = nodes.find(n => n.id === posteId)
        if (poste) {
          ruta.push({
            lat: poste.lat,
            lng: poste.lng,
            tipo: 'poste',
            nodeId: posteId
          })
        }
      })

      // Agregar destino
      ruta.push({ 
        lat: destinoNode!.lat, 
        lng: destinoNode!.lng, 
        tipo: 'terminal',
        nodeId: destinoNodeId
      })

      const longitudTotal = calcularLongitudRuta(ruta)

      const numFibras = parseInt(formData.numeroFibras)
      const tieneBuffers = formData.tipo !== 'DROP' && numFibras > 2

      // Generar nombre automático si no se proporcionó
      const origenNombre = (origenNode as any)?.nombre || origenNode?.tipo || 'Origen'
      const destinoNombre = (destinoNode as any)?.nombre || destinoNode?.tipo || 'Destino'
      const nombreAuto = formData.nombre || `${formData.tipo}-${numFibras}FO ${origenNombre} a ${destinoNombre}`
      
      // Generar código automático si no se proporcionó
      const timestamp = Date.now().toString(36).toUpperCase().slice(-4)
      const codigoAuto = formData.codigo || `${formData.tipo.slice(0, 3)}-${numFibras}-${timestamp}`

      // FASE 1: Crear cable sin fibras para obtener UUID real del servidor
      const cableBasico = {
        nombre: nombreAuto,
        codigo: codigoAuto,
        tipo: formData.tipo,
        numeroFibras: numFibras,
        fabricante: formData.fabricante || undefined,
        modelo: formData.modelo || undefined,
        ruta,
        postesIds: postesIntermmedios,
        longitudTotal,
        longitudUsada: 0,
        tieneBuffers,
        buffers: null,
        fibras: null,
        estado: 'ACTIVO',
        origenId: origenNodeId,
        destinoId: destinoNodeId,
        // Información de conexión DROP (NAP → Cliente)
        ...(isDropConnection && {
          conexionDrop: {
            napId: napNode?.id,
            clienteId: clienteNode?.id,
            puertoNAP: parseInt(dropConfig.puertoNAP),
            colorPigtail: dropConfig.colorPigtail,
            clienteNombre: (clienteNode as any)?.nombre || 'Cliente',
          }
        })
      } as any

      // Crear cable y obtener UUID real
      const createdCable = await addCable(cableBasico)
      const cableIdReal = (createdCable as any)?.id

      // FASE 2: Generar estructura de fibras con UUID real del cable
      if (cableIdReal) {
        const { buffers, fibras } = generarEstructuraFibras(cableIdReal, numFibras, tieneBuffers)
        
        // Actualizar el cable con la estructura de fibras correcta
        await updateCable({
          ...(createdCable as any),
          buffers,
          fibras,
        })
      }

      // Si es conexión DROP, actualizar el puerto de la NAP como ocupado
      if (isDropConnection && napNode && dropConfig.puertoNAP && cableIdReal) {
        const puertoNum = parseInt(dropConfig.puertoNAP)
        const napConfig = (napNode as any).config || {}
        const puertosActuales = napConfig.puertos || []
        
        // Crear o actualizar el array de puertos
        const nuevaCapacidad = napConfig.numPuertos || Math.max(8, puertoNum)
        const nuevosPuertos = Array.from({ length: nuevaCapacidad }, (_, i) => {
          const puertoExistente = puertosActuales.find((p: any) => p.puerto === i + 1)
          if (i + 1 === puertoNum) {
            // Este es el puerto que se está ocupando
            return {
              puerto: puertoNum,
              estado: 'ocupado',
              clienteId: clienteNode?.id,
              clienteNombre: (clienteNode as any)?.nombre || 'Cliente',
              cableId: cableIdReal,
              colorPigtail: dropConfig.colorPigtail,
              fechaInstalacion: new Date().toISOString(),
            }
          }
          return puertoExistente || {
            puerto: i + 1,
            estado: 'libre',
            clienteId: null,
          }
        })

        // Actualizar la NAP con los nuevos puertos
        try {
          await updateNode({
            ...napNode,
            config: {
              ...napConfig,
              puertos: nuevosPuertos,
            }
          } as any)
        } catch (napError) {
          console.warn('Error actualizando puerto NAP:', napError)
        }
      }

      onClose()
    } catch (error) {
      console.error('Error al guardar cable:', error)
      alert('Error al guardar el cable: ' + (error instanceof Error ? error.message : 'Error desconocido'))
    } finally {
      setLoading(false)
    }
  }

  const handleTipoChange = (tipo: string) => {
    setFormData(prev => ({ ...prev, tipo }))

    // Configuraciones sugeridas por tipo
    switch (tipo) {
      case 'TRONCAL':
        setFormData(prev => ({
          ...prev,
          tipo,
          numeroFibras: '144',
        }))
        break
      case 'DISTRIBUCION':
        setFormData(prev => ({
          ...prev,
          tipo,
          numeroFibras: '48',
        }))
        break
      case 'ACOMETIDA':
        setFormData(prev => ({
          ...prev,
          tipo,
          numeroFibras: '12',
        }))
        break
      case 'DROP':
        setFormData(prev => ({
          ...prev,
          tipo,
          numeroFibras: '1',
        }))
        break
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-stretch md:items-center justify-center z-[9999] md:p-4">
      <div
        className="bg-white dark:bg-dark-800 md:rounded-lg shadow-2xl w-full max-w-xl h-full md:h-auto md:max-h-[90vh] overflow-y-auto"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CableIcon className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold">
              Nuevo Cable
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Información de conexión y ruta */}
          {(origenNode || destinoNode) && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="font-semibold text-sm text-blue-900 dark:text-blue-100 mb-3">
                Ruta del Cable
              </h3>
              
              {/* Visualización de la ruta */}
              <div className="flex items-center flex-wrap gap-2 text-sm mb-3">
                {/* Origen */}
                {origenNode && (
                  <div className="flex items-center gap-1 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                    <span className="text-green-600">📍</span>
                    <span className="font-medium text-green-800 dark:text-green-200">
                      {origenNode.nombre || origenNode.tipo}
                    </span>
                  </div>
                )}

                {/* Postes intermedios */}
                {postesIntermmedios.map((posteId, idx) => {
                  const poste = nodes.find(n => n.id === posteId)
                  return (
                    <div key={posteId} className="flex items-center gap-1">
                      <span className="text-gray-400">→</span>
                      <div className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded flex items-center gap-1">
                        <span>📌</span>
                        <span className="text-xs">{poste?.nombre || `Poste ${idx + 1}`}</span>
                      </div>
                    </div>
                  )
                })}

                {/* Destino */}
                {destinoNode && (
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">→</span>
                    <div className="bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded flex items-center gap-1">
                      <span className="text-red-600">🎯</span>
                      <span className="font-medium text-red-800 dark:text-red-200">
                        {destinoNode.nombre || destinoNode.tipo}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Resumen */}
              <div className="text-xs text-gray-600 dark:text-gray-400 border-t border-blue-200 dark:border-blue-700 pt-2 mt-2">
                <span className="font-medium">{postesIntermmedios.length}</span> poste(s) de paso
                {postesIntermmedios.length > 0 && (
                  <span className="ml-2 text-blue-600 dark:text-blue-400">
                    (Cable continuo, sin cortes)
                  </span>
                )}
              </div>

              {errors.origen && <p className="text-red-500 text-xs mt-2">{errors.origen}</p>}
              {errors.destino && <p className="text-red-500 text-xs">{errors.destino}</p>}
            </div>
          )}

          {/* Nombre y Código del Cable */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Nombre del Cable
                <span className="text-gray-400 font-normal ml-1">(opcional)</span>
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                placeholder={`Ej: FO-24 Centro a NAP1`}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600 text-sm"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Si no se ingresa, se generará automáticamente
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Código
                <span className="text-gray-400 font-normal ml-1">(opcional)</span>
              </label>
              <input
                type="text"
                value={formData.codigo}
                onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value.toUpperCase() }))}
                placeholder={`Ej: CAB-001`}
                className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600 text-sm uppercase"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Código corto para identificación rápida
              </p>
            </div>
          </div>

          {/* Tipo de cable */}
          <div>
            <label className="block text-sm font-medium mb-2">Tipo de Cable *</label>
            <div className="grid grid-cols-4 gap-2">
              {(['TRONCAL', 'DISTRIBUCION', 'ACOMETIDA', 'DROP'] as const).map((tipo) => (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => handleTipoChange(tipo)}
                  disabled={loading}
                  className={`p-3 rounded-lg border-2 transition-all text-xs font-medium ${
                    formData.tipo === tipo
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-primary-300'
                  } disabled:opacity-50`}
                >
                  {tipo}
                </button>
              ))}
            </div>
          </div>

          {/* Configuración de conexión DROP (NAP → Cliente) */}
          {isDropConnection && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Plug className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-sm text-purple-900 dark:text-purple-100">
                  Conexión DROP: Puerto NAP → Cliente
                </h3>
              </div>

              {/* Info del cliente */}
              <div className="flex items-center gap-3 bg-white dark:bg-dark-700 rounded-lg p-3">
                <User className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-medium">{(clienteNode as any)?.nombre || 'Cliente'}</p>
                  <p className="text-xs text-gray-500">
                    {(clienteNode as any)?.descripcion || `ID: ${clienteNode?.id?.slice(-8)}`}
                  </p>
                </div>
              </div>

              {/* Selección de Puerto NAP */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Puerto de la NAP * <span className="text-gray-500 font-normal">({(napNode as any)?.nombre || 'NAP'})</span>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: numPuertos }, (_, i) => {
                    const puerto = i + 1
                    const puertoInfo = puertosNAP.find((p: any) => p.puerto === puerto)
                    const ocupado = puertoInfo?.estado === 'ocupado' || puertoInfo?.clienteId
                    const isSelected = dropConfig.puertoNAP === String(puerto)
                    
                    return (
                      <button
                        key={puerto}
                        type="button"
                        onClick={() => !ocupado && setDropConfig(prev => ({ ...prev, puertoNAP: String(puerto) }))}
                        disabled={ocupado || loading}
                        className={`
                          p-2 rounded-lg border-2 text-sm font-medium transition-all
                          ${isSelected
                            ? 'border-purple-500 bg-purple-100 dark:bg-purple-900/40 text-purple-700'
                            : ocupado
                              ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'border-gray-200 hover:border-purple-300 dark:border-dark-600'
                          }
                        `}
                        title={ocupado ? `Puerto ${puerto} - Ocupado` : `Puerto ${puerto} - Disponible`}
                      >
                        <div className="text-center">
                          <span className={`text-lg ${ocupado ? 'opacity-50' : ''}`}>
                            {ocupado ? '🔴' : isSelected ? '🟢' : '⚪'}
                          </span>
                          <div className="text-xs mt-1">P{puerto}</div>
                        </div>
                      </button>
                    )
                  })}
                </div>
                {errors.puertoNAP && (
                  <p className="text-red-500 text-xs mt-2">{errors.puertoNAP}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  🟢 Seleccionado | ⚪ Disponible | 🔴 Ocupado
                </p>
              </div>

              {/* Color del pigtail (opcional) */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Color del Pigtail
                </label>
                <select
                  value={dropConfig.colorPigtail}
                  onChange={(e) => setDropConfig(prev => ({ ...prev, colorPigtail: e.target.value }))}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600"
                >
                  <option value="azul">🔵 Azul</option>
                  <option value="naranja">🟠 Naranja</option>
                  <option value="verde">🟢 Verde</option>
                  <option value="marron">🟤 Marrón</option>
                  <option value="gris">⚪ Gris</option>
                  <option value="blanco">⬜ Blanco</option>
                  <option value="rojo">🔴 Rojo</option>
                  <option value="negro">⚫ Negro</option>
                  <option value="amarillo">🟡 Amarillo</option>
                  <option value="violeta">🟣 Violeta</option>
                  <option value="rosa">💗 Rosa</option>
                  <option value="turquesa">💠 Turquesa</option>
                </select>
              </div>

              {/* Resumen de conexión */}
              {dropConfig.puertoNAP && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    ✓ Conexión configurada:
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                    <strong>{(napNode as any)?.nombre || 'NAP'}</strong> Puerto {dropConfig.puertoNAP} → <strong>{(clienteNode as any)?.nombre || 'Cliente'}</strong>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Configuración básica */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-gray-600 dark:text-gray-400">
              Configuración del Cable
            </h3>

            {/* Número de fibras */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Número Total de Fibras *
              </label>
              <input
                type="number"
                value={formData.numeroFibras}
                onChange={(e) => setFormData(prev => ({ ...prev, numeroFibras: e.target.value }))}
                disabled={loading || isDropConnection}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600 ${
                  errors.numeroFibras ? 'border-red-500' : 'border-gray-300'
                } disabled:opacity-50`}
                min="1"
                max="288"
                placeholder="Ej: 1, 2, 6, 12, 24, 48"
              />
              {errors.numeroFibras && (
                <p className="text-red-500 text-xs mt-1">{errors.numeroFibras}</p>
              )}
              {isDropConnection ? (
                <p className="text-xs text-purple-600 mt-1">
                  Cable DROP: 1 fibra (unifibra) por defecto
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Valores comunes: 1 (unifibra), 2, 6, 12, 24, 48, 96, 144
                </p>
              )}
            </div>

            {/* Fabricante (opcional) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Fabricante (opcional)
              </label>
              <input
                type="text"
                value={formData.fabricante}
                onChange={(e) => setFormData(prev => ({ ...prev, fabricante: e.target.value }))}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600 disabled:opacity-50"
                placeholder="Ej: Corning, Furukawa, Prysmian"
              />
            </div>

            {/* Modelo (opcional) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Modelo (opcional)
              </label>
              <input
                type="text"
                value={formData.modelo}
                onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600 disabled:opacity-50"
                placeholder="Ej: OptiTip 48F"
              />
            </div>
          </div>

          {/* Información adicional por tipo */}
          <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
            <h4 className="font-medium text-sm mb-2">Características del tipo "{formData.tipo}"</h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
              {formData.tipo === 'TRONCAL' && (
                <>
                  <li>• Alta capacidad (96-288 fibras)</li>
                  <li>• Conexión entre POPs principales</li>
                  <li>• Requiere empalmes en cierres</li>
                </>
              )}
              {formData.tipo === 'DISTRIBUCION' && (
                <>
                  <li>• Capacidad media (24-96 fibras)</li>
                  <li>• Desde POP/Cierre hacia NAPs</li>
                  <li>• Más común en redes FTTH</li>
                </>
              )}
              {formData.tipo === 'ACOMETIDA' && (
                <>
                  <li>• Baja capacidad (2-12 fibras)</li>
                  <li>• Desde NAP hacia edificios/clientes</li>
                  <li>• Instalación en fachadas</li>
                </>
              )}
              {formData.tipo === 'DROP' && (
                <>
                  <li>• Unifibra o bifibra (1-2 fibras)</li>
                  <li>• Conexión final NAP → Cliente ONT</li>
                  <li>• Cable de última milla, flexible</li>
                </>
              )}
            </ul>
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>Creando...</>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Crear Cable
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
